package xim.poc.game.configuration.v0.tactics.behaviors

import xim.poc.ActorManager
import xim.poc.ItemModelSlot
import xim.poc.ModelLook
import xim.poc.RoutineOptions
import xim.poc.game.ActorState
import xim.poc.game.CombatBonusAggregate
import xim.poc.game.MiscEffects
import xim.poc.game.configuration.SkillApplierHelper
import xim.poc.game.configuration.constants.*
import xim.poc.game.configuration.v0.GameV0
import xim.poc.game.configuration.v0.TacticsInstinct
import xim.poc.game.configuration.v0.tactics.*
import xim.poc.game.configuration.v0.tactics.TacticsInstincts.ignoresEnmity
import xim.poc.game.configuration.v0.tactics.TacticsInstincts.pass
import xim.poc.game.event.Event
import xim.poc.gl.PortraitBufferManager
import xim.poc.ui.ShiftJis
import xim.resource.DatId
import xim.util.addInPlace
import kotlin.time.Duration.Companion.seconds

enum class AvatarConfig(val summonSkill: SkillId, val modelLook: ModelLook, val summonAnimation: Int, val staffIndex: Int, val astralFlow: SkillId) {
    Ifrit(spellIfrit_298, ModelLook.npc(0x12), 0xD4A, 329, mskillInferno_592),
    Titan(spellTitan_299, ModelLook.npc(0x13), 0xD4B, 331, mskillEarthenFury_601),
    Leviathan(spellLeviathan_300, ModelLook.npc(0x14), 0xD4C, 325, mskillTidalWave_610),
    Garuda(spellGaruda_301, ModelLook.npc(0x15), 0xD4D, 326, mskillAerialBlast_619),
    Shiva(spellShiva_302, ModelLook.npc(0x16), 0xD4E, 327, mskillDiamondDust_628),
    Ramuh(spellRamuh_303, ModelLook.npc(0x17), 0xD4F, 328, mskillJudgmentBolt_637),
}

class TacticsShantottoBehavior(actorState: ActorState): TacticsBehaviorController(actorState) {

    override fun onBehaviorInitialized(): List<Event> {
        actorState.getUnit().instincts += listOf(
            TacticsInstinct(pass.id, 0),
            TacticsInstinct(ignoresEnmity.id, 0),
        )
        return emptyList()
    }

    override fun estimateSkillValue(query: TacticsSkillQuery): Float {
        val baseScore = super.estimateSkillValue(query)
        if (query.skill != mskillEmpiricalResearch_3486) { return baseScore }
        return if (query.result.allTargetIds.size > 1) { baseScore } else { 0f }
    }

    override fun behaviorEnmityMultiplier(): Float {
        return 0f
    }

}

class TacticsShantottoClone(val config: AvatarConfig, actorState: ActorState): TacticsBehaviorController(actorState) {

    private var turnCounter = 0

    override fun onBehaviorInitialized(): List<Event> {
        actorState.getBaseLook().equipment[ItemModelSlot.Main] = config.staffIndex

        val adjustedName = actorState.name + ShiftJis.colorClear + (config.summonSkill.tacticsElement().toCharacter() ?: "")
        actorState.name = adjustedName
        actorState.getUnit().name = adjustedName

        return emptyList()
    }

    override fun onTurnStart(match: TacticsMatchInstance) {
        if (actorState.getCursorPosition() is PanelPosition) { turnCounter += 1 }
    }

    override fun applyTacticsBehaviorBonuses(aggregate: CombatBonusAggregate) {
        val resists = jobResistanceTables[config.summonSkill.tacticsElement()] ?: return
        resists.forEach { aggregate.elementalResistance.addInPlace(it.key, it.value) }
    }

    override fun getSelectableSkills(): List<SkillId> {
        return if (hasUsedSkill(config.summonSkill)) {
            listOf(config.astralFlow)
        } else if (wantsToTransform()) {
            listOf(config.summonSkill)
        } else {
            super.getSkills() - setOf(config.astralFlow, config.summonSkill)
        }
    }

    override fun estimateSkillValue(query: TacticsSkillQuery): Float {
        if (query.skill != config.summonSkill) { return super.estimateSkillValue(query) }
        val distance = query.originalPosition.distance(query.destinationPosition)
        return if (distance == 0) { 1f } else { 0f }
    }

    override fun getMoveForwardCost(destination: CursorPosition?): Int {
        return if (destination is EnemyPrepPosition) { 400 } else { 200 }
    }

    private fun wantsToTransform(): Boolean {
        return !hasUsedSkill(config.summonSkill) && (actorState.getHpp() <= 0.5f || turnCounter >= 4)
    }

}

class BecomeAvatarEvent(val avatarConfig: AvatarConfig, val context: SkillApplierHelper.TargetEvaluatorContext): Event {
    override fun apply(): List<Event> {
        val actor = ActorManager[context.targetState.id]

        val turn = GameV0.getCurrentTacticsZoneInstance()?.getMatch()?.getCurrentTurn() ?: throw IllegalStateException()
        val turnLock = turn.lock()

        actor?.enqueueModelRoutine(DatId.specialDeath, options = RoutineOptions {
            context.targetState.setBaseLook(avatarConfig.modelLook)
            MiscEffects.playEffect(actor, actor, fileTableIndex = avatarConfig.summonAnimation, DatId.main)

            turnLock.locked = false
            turn.onSkillAnimationComplete(endWait = 5.seconds)

            context.targetState.getUnit().name = avatarConfig.name
            context.targetState.name = avatarConfig.name

            PortraitBufferManager.submitPhotoRequest(context.targetState.id)
        })

        return emptyList()
    }
}